import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Box, SxProps, Theme } from '@mui/material';

interface MarkdownRendererProps {
  children: string | null | undefined;
  sx?: SxProps<Theme>;
}

/**
 * Wrapper component for rendering Markdown content with consistent styling using MUI theme
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children, sx }) => {
  if (!children) return null;
  
  return (
    <Box
      sx={{
        // Only style elements WITHOUT inline styles
        '& h1:not([style])': { 
          fontSize: '1.25rem', 
          fontWeight: 'bold', 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& h2:not([style])': { 
          fontSize: '1.19rem', 
          fontWeight: 500, 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& h3:not([style])': { 
          fontSize: '1.13rem', 
          fontWeight: 500, 
          fontStyle: 'italic', 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& h4:not([style])': { 
          fontSize: '1rem', 
          fontWeight: 500, 
          fontStyle: 'italic', 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& h5:not([style])': { 
          fontSize: '1.13rem', 
          fontStyle: 'italic', 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& p:not([style])': { 
          marginTop: 0, 
          marginBottom: '0.5rem',
          color: 'text.primary'
        },
        '& p:first-of-type:not([style])': { marginTop: 0 },
        '& p:last-of-type:not([style])': { marginBottom: 0 },
        '& ul:not([style]), & ol:not([style])': { 
          marginTop: '0.5rem', 
          marginBottom: '0.5rem', 
          paddingLeft: '1.5rem',
          color: 'text.primary'
        },
        '& li:not([style])': { 
          marginBottom: '0.25rem',
          color: 'text.primary'
        },
        '& code:not([style])': { 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
          padding: '0.125rem 0.25rem', 
          borderRadius: '0.25rem', 
          fontSize: '0.875em',
          color: 'text.primary',
          fontFamily: 'monospace'
        },
        '& pre:not([style])': { 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
          padding: '0.75rem', 
          borderRadius: '0.25rem', 
          overflow: 'auto',
          '& code': {
            backgroundColor: 'transparent',
            padding: 0
          }
        },
        '& blockquote:not([style])': { 
          borderLeft: (theme) => `4px solid ${theme.palette.divider}`, 
          paddingLeft: '1rem', 
          marginLeft: 0, 
          fontStyle: 'italic',
          color: 'text.secondary'
        },
        '& a': {
          '&:not([style*="color"])': {
            color: 'primary.main'
          },
          '&:not([style*="text-decoration"])': {
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        },
        '& table:not([style])': {
          borderCollapse: 'collapse',
          width: '100%',
          marginTop: '0.5rem',
          marginBottom: '0.5rem'
        },
        '& th:not([style]), & td:not([style])': {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          padding: '0.5rem',
          textAlign: 'left'
        },
        '& th:not([style])': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          fontWeight: 600
        },
        '& hr:not([style])': {
          border: 'none',
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          marginTop: '1rem',
          marginBottom: '1rem'
        },
        ...sx
      }}
    >
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};
